import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Plus
} from 'lucide-react';
import { Feedback, FeedbackStatus, FeedbackPriority } from '../types';
import { Button } from './ui';
import { FeedbackCard } from './FeedbackCard';
import { useUser } from '../contexts/UserContext';
import { 
  subscribeToFeedbackByHotel, 
  updateFeedback, 
  addFeedbackComment,
  deleteFeedbackComment
} from '../firebase/firestore';
import { logger } from '../utils/logger';

interface FeedbackSectionProps {
  hotelId: string;
  hotelName: string;
  onShowForm: () => void;
  onEdit: (feedback: Feedback) => void;
  onDelete: (feedbackId: string) => void;
  onDeleteComment?: (feedbackId: string, commentId: string) => void;
}

// Filter options removed for simplified structure

export function FeedbackSection({ hotelId, hotelName, onShowForm, onEdit, onDelete, onDeleteComment }: FeedbackSectionProps) {
  const { user } = useUser();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  // Edit functionality moved to parent component
  
  // Filters (simplified - just show all feedback for now)
  const [searchTerm] = useState('');

  // Subscribe to feedback updates
  useEffect(() => {
    const unsubscribe = subscribeToFeedbackByHotel(hotelId, (feedbacks) => {
      setFeedbacks(feedbacks);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [hotelId]);

  // Filter and sort feedbacks (simplified)
  const filteredAndSortedFeedbacks = feedbacks
    .sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(); // Most recent first
    });

  // Feedback submission is now handled by the parent component

  // Delete functionality moved to parent component

  const handleAddComment = async (feedbackId: string, comment: string) => {
    try {
      if (!user) return;
      
      const commentData = {
        content: comment,
        author: user.id,
        authorEmail: user.email,
        authorName: user.displayName,
      };
      
      await addFeedbackComment(feedbackId, commentData);
    } catch (error) {
      logger.error('Error adding comment:', error);
      throw error;
    }
  };

  const handleUpdateStatus = async (feedbackId: string, status: FeedbackStatus) => {
    try {
      await updateFeedback(feedbackId, { status });
    } catch (error) {
      logger.error('Error updating feedback status:', error);
    }
  };

  const handleUpdatePriority = async (feedbackId: string, priority: FeedbackPriority) => {
    try {
      await updateFeedback(feedbackId, { priority });
    } catch (error) {
      logger.error('Error updating feedback priority:', error);
    }
  };

  const handleMarkAsRead = async (feedbackId: string) => {
    try {
      await updateFeedback(feedbackId, { isReadByAdmin: true });
    } catch (error) {
      logger.error('Error marking feedback as read:', error);
    }
  };

  const handleDeleteComment = async (feedbackId: string, commentId: string) => {
    try {
      await deleteFeedbackComment(feedbackId, commentId);
    } catch (error) {
      logger.error('Error deleting comment:', error);
      throw error;
    }
  };

  // Edit functionality moved to parent component

  // Stats (removed since we're not showing them in this component anymore)
  // const stats = {
  //   total: feedbacks.length,
  //   open: feedbacks.filter(f => f.status === 'open').length,
  //   inProgress: feedbacks.filter(f => f.status === 'in-progress').length,
  //   resolved: feedbacks.filter(f => f.status === 'resolved').length,
  //   critical: feedbacks.filter(f => f.priority === 'critical').length,
  // };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

    return (
    <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-8 border-b border-white/20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-display font-bold text-gray-900">Feedback Management</h2>
            <p className="text-lg text-gray-600 font-text">
              Share your thoughts and report issues for {hotelName}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Quick Actions */}
            <div className="flex items-center space-x-2">
              <Button 
                variant="primary" 
                size="sm"
                onClick={onShowForm}
                className="shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit Feedback
              </Button>
            </div>
            
            {/* Feedback Counter */}
            <div className="bg-white/30 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/20">
              <div className="flex items-center space-x-3 text-gray-700">
                <MessageSquare className="w-5 h-5" />
                <span className="font-text font-semibold">
                  {feedbacks.length} feedback items
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-8">
        {/* Feedback List */}
        {filteredAndSortedFeedbacks.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-display font-bold text-gray-700 mb-3">
              No Feedback Yet
            </h3>
            <p className="text-lg text-gray-500 font-text max-w-md mx-auto leading-relaxed mb-6">
              This hotel doesn't have any feedback yet. Be the first to share your thoughts and help improve the system!
            </p>
            {!searchTerm && (
              <Button 
                onClick={onShowForm}
                className="shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit First Feedback
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredAndSortedFeedbacks.map((feedback) => (
                              <FeedbackCard
                  key={feedback.id}
                  feedback={feedback}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAddComment={handleAddComment}
                  onDeleteComment={handleDeleteComment}
                  onUpdateStatus={handleUpdateStatus}
                  onUpdatePriority={handleUpdatePriority}
                  onMarkAsRead={handleMarkAsRead}
                />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
